import type {
  AnalysisDiff,
  SaveAnalysisRequest,
  SavedAnalysisDetail,
  SavedAnalysisListItem,
} from '@smartapply/shared';

import { SmartApplyApiError } from './api-client.js';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3030';

let csrfTokenCache: string | null = null;

async function ensureCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  const res = await fetch(`${API_URL}/api/v1/csrf-token`, { credentials: 'include' });
  const data = (await res.json()) as { csrfToken: string };
  csrfTokenCache = data.csrfToken;
  return data.csrfToken;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = await ensureCsrfToken();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;
  if (res.ok) return (await res.json()) as T;
  let payload: { error?: { code: string; message: string } } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* ignore */
  }
  throw new SmartApplyApiError(
    payload.error?.code ?? `http_${res.status}`,
    payload.error?.message ?? `HTTP ${res.status}`,
    res.status,
  );
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (res.ok) return (await res.json()) as T;
  let payload: { error?: { code: string; message: string } } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* ignore */
  }
  throw new SmartApplyApiError(
    payload.error?.code ?? `http_${res.status}`,
    payload.error?.message ?? `HTTP ${res.status}`,
    res.status,
  );
}

async function deleteJson(path: string): Promise<void> {
  const token = await ensureCsrfToken();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': token },
    credentials: 'include',
  });
  if (res.status === 204 || res.ok) return;
  let payload: { error?: { code: string; message: string } } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* ignore */
  }
  throw new SmartApplyApiError(
    payload.error?.code ?? `http_${res.status}`,
    payload.error?.message ?? `HTTP ${res.status}`,
    res.status,
  );
}

export async function saveAnalysis(
  req: SaveAnalysisRequest,
): Promise<{ id: string; createdAt: string }> {
  return postJson('/api/v1/analyses', req);
}

export async function listAnalyses(
  includeArchived = false,
): Promise<{ items: SavedAnalysisListItem[] }> {
  const q = includeArchived ? '?archived=true' : '';
  return getJson(`/api/v1/analyses${q}`);
}

export async function getAnalysis(id: string): Promise<SavedAnalysisDetail> {
  return getJson(`/api/v1/analyses/${id}`);
}

export async function archiveAnalysis(id: string): Promise<void> {
  await postJson(`/api/v1/analyses/${id}/archive`, {});
}

export async function deleteAnalysis(id: string): Promise<void> {
  await deleteJson(`/api/v1/analyses/${id}`);
}

export async function setAnalysisLabel(id: string, label: string | null): Promise<void> {
  await postJson(`/api/v1/analyses/${id}/label`, { label });
}

export async function compareAnalyses(aId: string, bId: string): Promise<AnalysisDiff> {
  const params = new URLSearchParams({ a: aId, b: bId });
  return getJson(`/api/v1/analyses/compare?${params.toString()}`);
}

// ============================================================
// Admin — gestão de sugestões
// ============================================================

export interface AdminSuggestion {
  id: string;
  name: string | null;
  email: string | null;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  adminNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
  roadmapItemId: string | null;
}

export async function listAdminSuggestions(): Promise<{ items: AdminSuggestion[] }> {
  return getJson('/api/v1/admin/suggestions');
}

export async function approveSuggestion(
  id: string,
  payload: {
    title: string;
    description: string;
    category: 'ux' | 'ai' | 'integration' | 'performance' | 'billing' | 'other';
    status: 'considered' | 'planned' | 'in_progress';
    adminNote?: string;
  },
): Promise<{ ok: true; roadmapItemId: string }> {
  return postJson(`/api/v1/admin/suggestions/${id}/approve`, payload);
}

export async function rejectSuggestion(
  id: string,
  adminNote?: string,
): Promise<{ ok: true }> {
  return postJson(`/api/v1/admin/suggestions/${id}/reject`, {
    adminNote: adminNote ?? undefined,
  });
}
